import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { walletAPI, stripeAPI, referralAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { useCountry } from '../lib/country-context';
import { toast } from 'sonner';
import {
  Wallet,
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  TrendingUp,
  Clock,
  DollarSign,
  Coins,
  History,
  X,
  Users,
  Gift,
  Copy,
  CheckCircle2,
  Share2,
} from 'lucide-react';

const TransactionItem = ({ transaction }) => {
  const isCredit = transaction.amount > 0;
  
  return (
    <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isCredit ? 'bg-green-100' : 'bg-red-100'
        }`}>
          {isCredit ? (
            <ArrowDownLeft className="w-5 h-5 text-green-600" />
          ) : (
            <ArrowUpRight className="w-5 h-5 text-red-600" />
          )}
        </div>
        <div>
          <p className="text-slate-800 font-medium">{transaction.description}</p>
          <p className="text-slate-500 text-sm">
            {new Date(transaction.created_at).toLocaleDateString('es', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
          {isCredit ? '+' : ''}{transaction.amount.toFixed(2)} {transaction.currency}
        </p>
      </div>
    </div>
  );
};

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const { country, formatCurrency, convertFromUSD } = useCountry();
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupMethod, setTopupMethod] = useState('wallet');
  const [processingTopup, setProcessingTopup] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
    checkStripeReturn();
  }, []);

  const loadData = async () => {
    try {
      const [transRes, refRes] = await Promise.all([
        walletAPI.getTransactions(),
        referralAPI.getStats().catch(() => ({ data: null }))
      ]);
      setTransactions(transRes.data);
      setReferralStats(refRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkStripeReturn = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      toast.info('Verificando pago...');
      await pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('No se pudo verificar el pago. Revisa tu email.');
      return;
    }

    try {
      const response = await stripeAPI.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('¡Pago exitoso! Tu balance ha sido actualizado.');
        refreshUser();
        loadData();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('La sesión de pago expiró.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    setProcessingTopup(true);
    try {
      if (topupMethod === 'stripe') {
        const response = await stripeAPI.createCheckout({
          amount: amount,
          currency: country.currency.toLowerCase(),
          service_type: 'topup',
          origin_url: window.location.origin
        });
        window.location.href = response.data.url;
      } else {
        await walletAPI.topup({ amount, currency: country.currency });
        toast.success(`¡Recarga de ${formatCurrency(amount)} exitosa!`);
        setShowTopup(false);
        setTopupAmount('');
        refreshUser();
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recargar');
    } finally {
      setProcessingTopup(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    setCopied(true);
    toast.success('Código copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = async () => {
    const shareData = {
      title: 'Únete a Mercapoli Move',
      text: `Usa mi código ${user?.referral_code} y gana DIGGII en Mercapoli Move!`,
      url: `https://app.mercapolimove.com/register?ref=${user?.referral_code}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy link
        await navigator.clipboard.writeText(shareData.url);
        toast.success('Link copiado al portapapeles');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const quickAmounts = [50, 100, 200, 500]; // In local currency

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-800 font-['Outfit']">Digi Wallet</h1>
          <p className="text-slate-500 mt-1">Gestiona tu billetera y tokens DIGGII</p>
        </div>

        {/* Balance Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Main Balance Card */}
          <div className="bg-gradient-to-br from-[#00BFA6] to-[#00887A] rounded-3xl p-6 relative overflow-hidden shadow-lg" data-testid="wallet-balance-card">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Balance {country.currency}</p>
                  <p className="text-3xl font-bold text-white">
                    {formatCurrency(convertFromUSD(user?.wallet_balance || 0))}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-white/70 text-sm">Digi Wallet • {country.name}</div>
                <div className="text-right">
                  <p className="text-white/60 text-xs">Equivalente USD</p>
                  <p className="text-white font-semibold">${user?.wallet_balance?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* DIGGII Card */}
          <Card className="card-elevated border-[#FFD166]/30" data-testid="diggii-balance-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#FFD166]/20 flex items-center justify-center">
                  <Coins className="w-6 h-6 text-[#FFD166]" />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Tokens DIGGII</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {user?.diggii_balance?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#FFD166]/10 rounded-xl">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#FFD166]" />
                  <span className="text-slate-600 text-sm">1 DIGGII = 1 USD</span>
                </div>
                <span className="text-[#00887A] font-semibold text-sm">≈ {formatCurrency(convertFromUSD(user?.diggii_balance || 0))}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button 
            onClick={() => setShowTopup(true)} 
            className="btn-primary flex-1 lg:flex-none"
            data-testid="topup-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Recargar
          </Button>
          <Button variant="outline" className="flex-1 lg:flex-none border-slate-200 text-slate-600" disabled>
            <ArrowUpRight className="w-5 h-5 mr-2" />
            Enviar
          </Button>
        </div>

        {/* Referral Card */}
        <Card className="card-elevated border-[#00BFA6]/20">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#00BFA6]" />
              Programa de Referidos DIGGII
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-slate-500 text-sm mb-3">Invita amigos y gana DIGGII por cada referido activo</p>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 bg-slate-50 border border-slate-200 p-4 rounded-xl">
                    <p className="text-slate-500 text-xs mb-1">Tu código</p>
                    <p className="text-xl font-bold text-[#00BFA6] font-mono">{user?.referral_code || 'N/A'}</p>
                  </div>
                  <Button
                    onClick={copyReferralCode}
                    className="h-full bg-[#00BFA6]/10 hover:bg-[#00BFA6]/20 text-[#00BFA6]"
                    data-testid="copy-referral-btn"
                  >
                    {copied ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
                <Button
                  onClick={shareReferral}
                  className="w-full btn-primary"
                  data-testid="share-referral-btn"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Invitar Amigos
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-xl text-center">
                  <Users className="w-6 h-6 text-[#00BFA6] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800">{referralStats?.total_referrals || 0}</p>
                  <p className="text-slate-500 text-xs">Referidos</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl text-center">
                  <Coins className="w-6 h-6 text-[#FFD166] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800">{referralStats?.total_earned_diggii || 0}</p>
                  <p className="text-slate-500 text-xs">DIGGII Ganados</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#00BFA6]" />
              Métodos de Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-slate-50 p-4 rounded-xl flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00BFA6]/15 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-[#00BFA6]" />
                </div>
                <div>
                  <p className="text-slate-800 font-medium">Digi Wallet</p>
                  <p className="text-slate-500 text-sm">{country.currency} Balance</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
              </div>
              
              <div className="bg-slate-50 p-4 rounded-xl flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FFD166]/15 flex items-center justify-center">
                  <Coins className="w-6 h-6 text-[#FFD166]" />
                </div>
                <div>
                  <p className="text-slate-800 font-medium">DIGGII</p>
                  <p className="text-slate-500 text-sm">Token 1:1 USD</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
              </div>
              
              <div className="bg-slate-50 p-4 rounded-xl flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-slate-800 font-medium">Stripe</p>
                  <p className="text-slate-500 text-sm">Tarjeta crédito/débito</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <History className="w-5 h-5 text-[#00BFA6]" />
              Historial de Transacciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 skeleton rounded-xl" />
                ))}
              </div>
            ) : transactions.length > 0 ? (
              <div>
                {transactions.map((tx) => (
                  <TransactionItem key={tx.id} transaction={tx} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay transacciones</p>
                <p className="text-slate-400 text-sm mt-1">Tu historial aparecerá aquí</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Topup Modal */}
        {showTopup && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="card-elevated w-full max-w-md animate-slide-up">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800 font-['Outfit']">Recargar Wallet</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowTopup(false)}
                    className="text-slate-500 hover:text-slate-800"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Payment Method Tabs */}
                <Tabs value={topupMethod} onValueChange={setTopupMethod} className="mb-6">
                  <TabsList className="grid grid-cols-2 bg-slate-100">
                    <TabsTrigger value="wallet" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">
                      <Wallet className="w-4 h-4 mr-2" />
                      Demo
                    </TabsTrigger>
                    <TabsTrigger value="stripe" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Stripe
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Amount Input */}
                <div className="space-y-4 mb-6">
                  <Label className="text-slate-700">Monto a recargar ({country.currency})</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl font-semibold">{country.symbol}</span>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={topupAmount}
                      onChange={(e) => setTopupAmount(e.target.value)}
                      className="pl-12 h-14 text-2xl bg-white border-slate-200 text-slate-800 rounded-xl"
                      data-testid="topup-amount-input"
                    />
                  </div>

                  {/* Quick Amounts */}
                  <div className="flex gap-2">
                    {quickAmounts.map((amount) => (
                      <Button
                        key={amount}
                        variant="outline"
                        onClick={() => setTopupAmount(amount.toString())}
                        className={`flex-1 h-10 rounded-xl border-slate-200 ${
                          topupAmount === amount.toString() ? 'bg-[#00BFA6]/10 border-[#00BFA6] text-[#00BFA6]' : 'text-slate-600'
                        }`}
                      >
                        {country.symbol}{amount}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Info */}
                <div className="bg-slate-50 p-4 rounded-xl mb-6">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    {topupMethod === 'stripe' ? (
                      <>
                        <CreditCard className="w-4 h-4" />
                        <span>Pago seguro con Stripe</span>
                      </>
                    ) : (
                      <>
                        <Wallet className="w-4 h-4" />
                        <span>Recarga de demostración - Digi Wallet</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 h-12 border-slate-200 text-slate-600 rounded-xl"
                    onClick={() => setShowTopup(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleTopup}
                    disabled={processingTopup || !topupAmount}
                    className={`flex-1 h-12 rounded-xl ${
                      topupMethod === 'stripe' 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                        : 'btn-primary'
                    }`}
                    data-testid="confirm-topup-btn"
                  >
                    {processingTopup ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        {topupMethod === 'stripe' ? 'Pagar con Stripe' : `Recargar $${topupAmount || '0'}`}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
